import React from 'react';
import axios from 'axios';
import { Alert, Button, Card, Collapse, List, Modal, Spin, Tag, message } from 'antd';
import { CheckCircleOutlined, LoadingOutlined, PlayCircleOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import * as serverconfig from '../serverconn';
import { INSTITUTION_TERMS } from '../institutionCopy';

var CryptoJS = require('crypto-js');

const { Panel } = Collapse;
const antIcon = <LoadingOutlined style={{ fontSize: 32 }} spin />;
var sacco = '';

class BasicSystemSetupPanel extends React.Component {
  state = {
    loading: true,
    running: false,
    setupStatus: null,
    lastResult: null,
    setupError: null,
    autoRunAttempted: false,
  };

  getSaccoId = () => {
    if (!localStorage.getItem('sacco')) {
      return '';
    }
    return CryptoJS.AES.decrypt(localStorage.getItem('sacco'), 'my-secret-key@12345').toString(
      CryptoJS.enc.Utf8
    );
  };

  componentDidMount() {
    sacco = this.getSaccoId();
    this.loadStatus();
  }

  loadStatus = () => {
    const saccoId = this.getSaccoId();
    if (!saccoId) {
      const msg = INSTITUTION_TERMS.noOrganizationSelected;
      message.error(msg);
      this.setState({ loading: false, setupError: msg, setupStatus: null });
      return;
    }

    sacco = saccoId;
    this.setState({ loading: true, setupError: null });
    const form_data = new FormData();
    form_data.append('sacco', saccoId);

    axios
      .post(`${serverconfig.backendserverurl}/customqueries/get_basic_system_setup_status`, form_data)
      .then((res) => {
        this.setState({ setupStatus: res.data, loading: false, setupError: null }, () => {
          if (
            this.props.autoRunOnMount &&
            !res.data.ready &&
            !this.state.autoRunAttempted
          ) {
            this.setState({ autoRunAttempted: true });
            this.executeSetup(false);
          }
        });
      })
      .catch((err) => {
        const msg =
          (err.response && err.response.data && err.response.data.message) ||
          'Could not load setup status.';
        message.error(msg);
        this.setState({ loading: false, setupError: msg, setupStatus: null });
      });
  };

  executeSetup = (showSuccessMessage = true) => {
    const saccoId = this.getSaccoId();
    if (!saccoId) {
      message.error(INSTITUTION_TERMS.noOrganizationSelected);
      return Promise.resolve();
    }

    this.setState({ running: true });
    const form_data = new FormData();
    form_data.append('sacco', saccoId);

    return axios
      .post(`${serverconfig.backendserverurl}/customqueries/run_basic_system_setup`, form_data)
      .then((res) => {
        if (showSuccessMessage) {
          message.success(res.data.message);
        }
        this.setState({
          lastResult: res.data,
          setupStatus: res.data.summary.status,
          running: false,
          setupError: null,
        });
        if (this.props.onSetupComplete) {
          this.props.onSetupComplete(res.data);
        }
      })
      .catch((err) => {
        const msg =
          (err.response && err.response.data && err.response.data.message) || 'Setup failed.';
        message.error(msg);
        this.setState({ running: false, setupError: msg });
      });
  };

  runSetup = () => {
    Modal.confirm({
      title: 'Run basic system setup?',
      content: (
        <div>
          <p>This creates missing reference data only. Existing records are left unchanged.</p>
          <ul>
            <li>Account types (e.g. Individual, Group)</li>
            <li>ID types, currency, districts</li>
            <li>Full chart of accounts and core ledger accounts</li>
            <li>All company profile GL links (deposits, withdrawals, interest, dividends)</li>
            <li>Loan product accounts (issuance, repayments, fines, fees)</li>
            <li>Cashflow, reserves, and income statement profile lists</li>
            <li>Member subaccount ledger mappings</li>
          </ul>
        </div>
      ),
      okText: 'Yes, run setup',
      cancelText: 'Cancel',
      onOk: () => this.executeSetup(true),
    });
  };

  renderChecklist() {
    const { setupStatus } = this.state;
    return (
      <List
        size="small"
        bordered
        dataSource={setupStatus?.checks || []}
        renderItem={(item) => (
          <List.Item>
            <span>{item.label}</span>
            <span>
              {item.ok ? (
                <Tag icon={<CheckCircleOutlined />} color="success">
                  {item.count}/{item.minimum}
                </Tag>
              ) : (
                <Tag color="warning">
                  {item.count}/{item.minimum}
                </Tag>
              )}
            </span>
          </List.Item>
        )}
      />
    );
  }

  render() {
    const { loading, running, setupStatus, setupError } = this.state;
    const { showChecklistExpanded = true } = this.props;

    if (loading) {
      return (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin indicator={antIcon} />
          </div>
        </Card>
      );
    }

    if (setupError && !setupStatus) {
      return (
        <Card style={{ marginBottom: 16 }}>
          <Alert
            type="error"
            showIcon
            message="Could not load setup status"
            description={setupError}
            style={{ marginBottom: 16 }}
          />
          <Button icon={<ReloadOutlined />} onClick={this.loadStatus}>
            Retry
          </Button>
        </Card>
      );
    }

    const ready = setupStatus?.ready;
    const passedCount = (setupStatus?.checks || []).filter((c) => c.ok).length;
    const totalCount = (setupStatus?.checks || []).length;

    return (
      <Card style={{ marginBottom: 16 }}>
        <Alert
          type={ready ? 'success' : 'warning'}
          showIcon
          message={
            ready
              ? 'System reference data is ready. You can proceed to data imports.'
              : this.props.autoRunOnMount && running
                ? 'Setting up your institution automatically — creating accounts and linking all settings…'
                : 'Quick setup needed — we can create accounts and link all GL settings automatically.'
          }
          description={!ready ? `${passedCount}/${totalCount} setup checks passed.` : null}
          style={{ marginBottom: 16 }}
        />

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            marginBottom: showChecklistExpanded ? 16 : 0,
          }}
        >
          <Button
            type="primary"
            size="large"
            icon={<SettingOutlined />}
            loading={running}
            onClick={this.runSetup}
          >
            Run Basic System Setup
          </Button>
          <Button icon={<ReloadOutlined />} onClick={this.loadStatus} disabled={running}>
            Refresh status
          </Button>
          {ready && (
            <Tag color="success" style={{ fontSize: 14, padding: '4px 10px' }}>
              Ready
            </Tag>
          )}
        </div>

        {showChecklistExpanded && (
          <Collapse ghost defaultActiveKey={ready ? [] : ['checklist']}>
            <Panel header="View setup checklist" key="checklist">
              {this.renderChecklist()}
            </Panel>
          </Collapse>
        )}
      </Card>
    );
  }
}

export default BasicSystemSetupPanel;
